import {
  Button,
  Col,
  Empty,
  Popconfirm,
  Row,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import type { AnyObject } from "antd/es/_util/type";
import type { ColumnsType } from "antd/es/table";
import { ReloadOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { getProblemMessage, retry } from "../utils/http";

export interface ArchivedListViewProps<T> {
  readonly entityLabel: string;
  readonly loader: (input: {
    searchQuery?: unknown;
    pageNumber: number;
    pageSize: number;
  }) => Promise<{ data: T[]; totalRecords: number }>;
  readonly restore: (uid: string) => Promise<unknown>;
  readonly columns: ColumnsType<T>;
  readonly rowKey: (row: T) => string;
  readonly pageSize?: number;
}

// Admin brief 2026-11-03 — "archive, not delete". This panel renders the
// soft-deleted rows for a given entity + offers a Restore row action that
// hits POST /{entity}/{uid}/Restore. The host page sits a Switch above and
// swaps between the normal active grid and this one.
function ArchivedListView<T extends AnyObject>(props: ArchivedListViewProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = props.pageSize ?? 25;
  const [restoringUid, setRestoringUid] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    retry(() =>
      props.loader({
        searchQuery: { conditions: [] },
        pageNumber: page - 1,
        pageSize,
      }),
    )
      .then((r) => {
        setData(r.data ?? []);
        setTotal(r.totalRecords ?? 0);
      })
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load archived."),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  function onRestore(uid: string): void {
    setRestoringUid(uid);
    props
      .restore(uid)
      .then(() => {
        message.success(`${capitalize(props.entityLabel)} restored.`);
        load();
      })
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to restore."),
      )
      .finally(() => setRestoringUid(null));
  }

  const augmented: ColumnsType<T> = [
    ...props.columns,
    {
      title: "",
      key: "__restore",
      width: 120,
      fixed: "right",
      render: (_: unknown, row: T) => {
        const uid = props.rowKey(row);
        return (
          <Popconfirm
            title={`Restore this ${props.entityLabel}?`}
            description="The record will reappear in the active list."
            okText="Restore"
            onConfirm={() => onRestore(uid)}
          >
            <Button
              type="link"
              size="small"
              loading={restoringUid === uid}
              icon={<ReloadOutlined />}
            >
              Restore
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  if (!loading && data.length === 0) {
    return (
      <Col className="p-6">
        <Empty
          description={
            <Typography.Text type="secondary">
              No archived {props.entityLabel}s.
            </Typography.Text>
          }
        />
      </Col>
    );
  }

  return (
    <Col className="flex flex-auto flex-col gap-2 overflow-hidden">
      <Row justify="space-between" align="middle" className="px-1">
        <Typography.Text type="secondary">
          Archived {props.entityLabel}s — click Restore to bring one back.
        </Typography.Text>
        <Space>
          <Typography.Text type="secondary" className="text-xs">
            {total.toLocaleString()} archived
          </Typography.Text>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </Row>
      <Table<T>
        rowKey={(row) => props.rowKey(row)}
        size="small"
        dataSource={data}
        columns={augmented as any}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
        scroll={{ x: 900 }}
      />
    </Col>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Utility for columns that are just a date — host pages can import if useful.
export const dateCol = (dataIndex: string, title: string, width = 140) => ({
  title,
  dataIndex,
  width,
  render: (v?: string | null) =>
    v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—",
});

export default ArchivedListView;
